import { Link } from '@tanstack/react-router'
import { ScanFace } from 'lucide-react'

import { faceListLink } from './face-routes'
import { faceStatus } from './face-status'
import type { FaceScope } from '@/lib/api/grower-faces-api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { m } from '@/lib/paraglide/messages'
import { useGrowerFaces } from '@/hooks/use-grower-faces-query'

type Props = {
    growerId: string
    scope: FaceScope
}

/**
 * Thẻ tóm tắt trong CHI TIẾT HỘ: ai đã đăng ký và lối vào màn cấu hình.
 *
 * Cố ý không hiện ảnh ở đây — chi tiết hộ đã dài, và ảnh khuôn mặt là dữ liệu
 * nhạy cảm nên chỉ hiện ở màn người dùng chủ động mở.
 */
export function GrowerFacesCard({ growerId, scope }: Props) {
    const { data: faces = [], isLoading } = useGrowerFaces(growerId, scope)

    return (
        <Card className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-1.5">
                    <div className="text-base font-semibold leading-none">
                        {m.face_card_title()}
                    </div>
                    <p className="text-sm text-muted-foreground">
                        {m.face_card_desc()}
                    </p>
                </div>
                {faces.length > 0 ? (
                    <Badge variant="secondary" className="shrink-0">
                        {m.face_card_count({ count: faces.length })}
                    </Badge>
                ) : null}
            </div>

            {isLoading ? (
                <p className="text-sm text-muted-foreground">
                    {m.face_list_loading()}
                </p>
            ) : null}

            {!isLoading && faces.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                    {m.face_card_empty()}
                </p>
            ) : null}

            {faces.length > 0 ? (
                <ul className="space-y-2.5">
                    {faces.map((face) => {
                        const status = faceStatus(face.photo_count)
                        return (
                            <li
                                key={face.id}
                                className="flex items-center gap-3"
                            >
                                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                                    <ScanFace className="size-4" />
                                </span>
                                <span className="grow text-sm font-medium">
                                    {face.full_name}
                                </span>
                                <Badge
                                    variant={
                                        status.usable ? 'success' : 'warning'
                                    }
                                >
                                    {status.label}
                                </Badge>
                            </li>
                        )
                    })}
                </ul>
            ) : null}

            <Button asChild className="w-full md:w-auto">
                <Link {...faceListLink(growerId, scope)}>
                    <ScanFace className="size-4" />
                    {m.face_card_configure()}
                </Link>
            </Button>
        </Card>
    )
}
